import { useMemo } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'

import { Workshift } from '../../../../types/workshift'
import BottomLegend from './_Components/BottomLegend/GenericBottomLegend'
import GraphTimelineView from '../GraphTimeline/GraphTimelineView'
import GraphBrush from '../GraphBrush/GraphBrush'
import GenericLineGraph from './_Components/LineGraph/GenericLineGraph'
import GenericTimeBarGraph from './_Components/TimeBarGraph/GenericTimeBarGraph'
import {
  getMeasuresForWordSlug,
  WordMeasure,
} from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/influx/measures-utils'
import { ChartMetricsConfigs, ChartPlugin, ChartMeasure, ChartDerivedMeasure, BaseChartMeasureLine } from './_types'
import { InfluxSeriesMeasuresData } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/sharedV2'
import React from 'react'
import { useResolvedChartMetrics } from './_hooks/useResolvedChartMetrics'
import AwaitChartData from './_Components/Guards/AwaitChartData/AwaitChartData'
import useDataBrush from './_hooks/useDataBrush'
interface Props<MeasureSlugs extends string = string> extends ChartMetricsConfigs<MeasureSlugs>, WithTranslation {
  /**
   * The id of the asset the chart is representing.
   */
  id: string
  title?: string
  plugins?: Array<ChartPlugin<MeasureSlugs>>
  plant: string
  workShift: Workshift
  startDate?: Date
  endDate?: Date
}

export type GenericBinarySrcGraphProps = Props

function _MainChart<K extends string = string>(
  props: Props<K> & {
    plant: string
    workShift: Workshift
    measuresData: InfluxSeriesMeasuresData<K>
    measureConfigs: Required<ChartMetricsConfigs<K>>
  }
) {
  const { workShift, title, measuresData, measureConfigs } = props
  const { handleBrush, filteredData } = useDataBrush({ data: measuresData })

  const measuresRepresentedByLine = useMemo(() => {
    const _measures = [...measureConfigs.measures, ...measureConfigs.derivedMeasures]
    return _measures.filter(
      (m): m is (ChartMeasure<K> | ChartDerivedMeasure<K>) & BaseChartMeasureLine<K> => m.representation === 'line'
    )
  }, [measureConfigs.measures, measureConfigs.derivedMeasures])

  const measuresRepresentedByTimeBar = useMemo(() => {
    const binaryWordMeasures = measureConfigs.binaryWords.reduce<WordMeasure[]>(
      (acc, word) => [...acc, ...getMeasuresForWordSlug(word.type)],
      []
    )

    const timeBarMeasures = [...measureConfigs.measures, ...measureConfigs.derivedMeasures]
      .filter(m => m.representation === 'time-bar')
      .map(m => m.slug)

    return [...binaryWordMeasures, ...timeBarMeasures]
  }, [measureConfigs.binaryWords, measureConfigs.derivedMeasures, measureConfigs.measures])

  return (
    <>
      {title && (
        <div className="mb-3">
          <h3 className={'w-100'}>{title}</h3>
        </div>
      )}

      {measuresRepresentedByLine.length > 0 && (
        <>
          <GenericLineGraph
            additionalChartLines={measuresRepresentedByLine}
            data={filteredData}
            warnAreasData={measureConfigs.warnAreas}
            workShift={workShift}
          />
          <GraphTimelineView data={filteredData} xAxisNumber={true} paddingRight={60} />
        </>
      )}

      {measuresRepresentedByTimeBar.length > 0 && (
        <>
          {measuresRepresentedByTimeBar.map(m => (
            <GenericTimeBarGraph topMargin={true} measureSlug={m} data={filteredData} paddingRight={74} />
          ))}
          <GraphTimelineView data={filteredData} xAxisNumber={true} paddingRight={60} />
          <BottomLegend measuresSlugs={measuresRepresentedByTimeBar} />
        </>
      )}

      {measuresRepresentedByLine.length > 0 && (
        <>
          <GraphBrush data={measuresData} brushOnChange={handleBrush} paddingRight={70} />
          <GraphTimelineView data={measuresData} xAxisNumber={false} paddingRight={60} />
        </>
      )}
    </>
  )
}

const GenericBinarySrcGraphSkeleton = (props: Props) => {
  const { plugins, id, plant, t, workShift } = props

  const chartMeasures = useResolvedChartMetrics({
    assetId: id,
    measures: props.measures,
    binaryWords: props.binaryWords,
    warnAreas: props.warnAreas,
    derivedMeasures: props.derivedMeasures,
    plugins: plugins ?? [],
  })

  return (
    <AwaitChartData
      plant={plant}
      workShift={workShift}
      t={t}
      measures={chartMeasures.measures}
      binaryWords={chartMeasures.binaryWords}
      derivedMeasures={chartMeasures.derivedMeasures}
      assetId={id}
    >
      {measuresData => (
        <div className="col-md-12 col-sm-12 col-lg-12">
          <_MainChart
            {...props}
            plant={plant}
            workShift={workShift}
            measuresData={measuresData}
            measureConfigs={chartMeasures}
          />
          {plugins?.map(
            (p, idx) =>
              p.Component && (
                <p.Component key={idx} assetId={id} plant={plant} workShift={workShift} t={t} data={measuresData} />
              )
          )}
        </div>
      )}
    </AwaitChartData>
  )
}

export default withTranslation()(GenericBinarySrcGraphSkeleton)
